import { Transaction, Budget, Category } from '../types';

export interface BudgetRecommendation {
  type: 'warning' | 'info' | 'success';
  category: string;
  message: string;
  action?: string;
}

export const generateBudgetRecommendations = (
  transactions: Transaction[],
  budgets: Budget[],
  categories: Category[]
): BudgetRecommendation[] => {
  const recommendations: BudgetRecommendation[] = [];
  
  // Analyser les dépenses des 3 derniers mois
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  
  const recentTransactions = transactions.filter(t => 
    new Date(t.date) >= threeMonthsAgo && t.type === 'expense'
  );
  
  // Calculer les moyennes par catégorie
  const categoryAverages: Record<string, number> = {};
  recentTransactions.forEach(t => {
    if (!categoryAverages[t.category]) {
      categoryAverages[t.category] = 0;
    }
    categoryAverages[t.category] += t.amount;
  });
  
  // Moyenner sur 3 mois
  Object.keys(categoryAverages).forEach(category => {
    categoryAverages[category] = categoryAverages[category] / 3;
  });
  
  // Vérifier les budgets vs moyennes
  budgets.forEach(budget => {
    const avgSpending = categoryAverages[budget.category] || 0;
    
    if (avgSpending > budget.allocated * 1.2) {
      recommendations.push({
        type: 'warning',
        category: budget.category,
        message: `Vos dépenses moyennes (${avgSpending.toFixed(0)}€) dépassent votre budget de ${budget.allocated}€`,
        action: `Considérez augmenter le budget à ${Math.ceil(avgSpending * 1.1)}€`
      });
    } else if (avgSpending < budget.allocated * 0.7) {
      recommendations.push({
        type: 'success',
        category: budget.category,
        message: `Excellent contrôle des dépenses en ${budget.category}!`,
        action: `Vous économisez en moyenne ${(budget.allocated - avgSpending).toFixed(0)}€ par mois`
      });
    }
  });
  
  // Détecter les catégories sans budget avec des dépenses importantes
  Object.keys(categoryAverages).forEach(category => {
    const hasExistingBudget = budgets.some(b => b.category === category);
    const avgSpending = categoryAverages[category];
    
    if (!hasExistingBudget && avgSpending > 100) {
      recommendations.push({
        type: 'info',
        category,
        message: `Vous dépensez en moyenne ${avgSpending.toFixed(0)}€/mois en ${category}`,
        action: `Créez un budget de ${Math.ceil(avgSpending * 1.2)}€ pour mieux contrôler ces dépenses`
      });
    }
  });
  
  // Alerte spéciale pour les crédits
  const creditSpending = categoryAverages['Crédit'] || 0;
  if (creditSpending > 0) {
    const totalIncome = transactions
      .filter(t => t.type === 'income' && new Date(t.date) >= threeMonthsAgo)
      .reduce((sum, t) => sum + t.amount, 0) / 3;
    
    const creditRatio = (creditSpending / totalIncome) * 100;
    
    if (creditRatio > 30) {
      recommendations.push({
        type: 'warning',
        category: 'Crédit',
        message: `Les remboursements de crédit représentent ${creditRatio.toFixed(1)}% de vos revenus`,
        action: 'Attention au surendettement - considérez un rachat de crédit'
      });
    } else if (creditRatio > 20) {
      recommendations.push({
        type: 'info',
        category: 'Crédit',
        message: `Les crédits représentent ${creditRatio.toFixed(1)}% de vos revenus`,
        action: 'Surveiller ce ratio pour maintenir un équilibre financier'
      });
    }
  }
  
  return recommendations;
};

export const calculateSavingsRate = (transactions: Transaction[]): number => {
  const currentMonth = new Date();
  const monthlyTransactions = transactions.filter(t => {
    const tDate = new Date(t.date);
    return tDate.getMonth() === currentMonth.getMonth() && 
           tDate.getFullYear() === currentMonth.getFullYear();
  });
  
  const income = monthlyTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
    
  const expenses = monthlyTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);
    
  return income > 0 ? ((income - expenses) / income) * 100 : 0;
};
